import type { FontAwesome6ProSolidIconName } from "@react-native-vector-icons/fontawesome6-pro";
import { StatusBar } from "expo-status-bar";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BackHandler, Platform, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import { TEST_IDS } from "@/testIdDefinition";
import { Card, CardContent } from "../../components/ui/card";

const ITEMS: {
    key: "recurring" | "times" | "replies";
    icon: FontAwesome6ProSolidIconName;
}[] = [
    { key: "recurring", icon: "arrows-rotate" },
    { key: "times", icon: "clock" },
    { key: "replies", icon: "paperclip" },
];

export function WhatsNewOverlay({
    onContinue,
}: {
    onContinue: () => void | Promise<void>;
}) {
    const { t } = useTranslation();

    useEffect(() => {
        if (Platform.OS !== "android") {
            return;
        }
        const subscription = BackHandler.addEventListener(
            "hardwareBackPress",
            () => {
                onContinue();
                return true;
            },
        );
        return () => subscription.remove();
    }, [onContinue]);

    return (
        <View className="absolute inset-0 z-40 bg-primary" pointerEvents="auto">
            <StatusBar style="light" />
            <View className="mt-[200px] min-h-0 flex-1 rounded-t-3xl bg-background web:max-w-lg mx-auto web:max-h-[600px] web:rounded-3xl">
                <SafeAreaView className="flex-1" edges={["bottom"]}>
                    <View className="flex-1 px-6 web:mx-auto web:w-full web:max-w-lg">
                        <ScrollView
                            className="flex-1"
                            contentContainerClassName="gap-12 pt-10 pb-4"
                        >
                            <View className="items-center gap-4">
                                <View className="items-center justify-center p-4 rounded-full bg-primary-soft">
                                    <Icon
                                        name="sparkles"
                                        iconStyle="solid"
                                        size={28}
                                        className="text-primary"
                                    />
                                </View>
                                <Text
                                    variant="h2"
                                    className="text-center w-[80%]"
                                >
                                    {t("whatsNew.title")}
                                </Text>
                            </View>

                            <Card>
                                <CardContent className="gap-8">
                                    {ITEMS.map((item) => (
                                        <View
                                            key={item.key}
                                            className="flex-row items-start gap-3"
                                        >
                                            <View className="mt-0.5 items-center justify-center rounded-card bg-background p-2">
                                                <Icon
                                                    name={item.icon}
                                                    iconStyle="solid"
                                                    size={14}
                                                    className="text-secondary"
                                                />
                                            </View>
                                            <Text className="min-w-0 flex-1 shrink leading-6">
                                                {t(
                                                    `whatsNew.items.${item.key}`,
                                                )}
                                            </Text>
                                        </View>
                                    ))}
                                </CardContent>
                            </Card>
                        </ScrollView>

                        <View className="pb-4">
                            <Button
                                className="w-full"
                                onPress={onContinue}
                                testID={TEST_IDS[TEST_IDS.WHATS_NEW_CONTINUE]}
                            >
                                <Text>{t("whatsNew.continue")}</Text>
                            </Button>
                        </View>
                    </View>
                </SafeAreaView>
            </View>
        </View>
    );
}
